import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance, { isAxiosError } from 'utils/axios';
import type {
    CreateTieringConfigPayload,
    CreateTieringConfigResponse,
    DataDictionaryColumn,
    FetchActiveTieringConfigParams,
    FetchActiveTieringConfigResponse,
    FetchDataDictionaryColumnsParams,
    FetchDataDictionaryColumnsResponse,
    FetchTieringConfigByIdParams,
    FetchTieringConfigByIdResponse,
    FetchTieringConfigsParams,
    FetchTieringConfigsResponse,
    HospitalTieringConfig,
    HospitalTieringConfigListItem
} from '@/types/hospital-tiering';
import { getErrorMessage } from 'utils/error-message';
import { DEFAULT_ROWS_PER_PAGE } from '@/constant';

export const fetchActiveTieringConfig = createAsyncThunk<
    HospitalTieringConfig | null,
    FetchActiveTieringConfigParams,
    { rejectValue: string }
>('hospitalTiering/fetchActiveConfig', async ({ hospitalId }, { rejectWithValue }) => {
    try {
        const { data } = await axiosInstance.get<FetchActiveTieringConfigResponse>(`/admin/hospitals/${hospitalId}/tiering-configs/active`);
        return data?.item ?? null;
    } catch (error: unknown) {
        if (isAxiosError(error) && error.response?.status === 404) {
            return null;
        }
        return rejectWithValue(getErrorMessage(error, 'Failed to fetch active tiering config'));
    }
});

export const fetchTieringConfigs = createAsyncThunk<
    {
        items: HospitalTieringConfigListItem[];
        total: number;
        page: number;
        limit: number;
    },
    FetchTieringConfigsParams,
    { rejectValue: string }
>('hospitalTiering/fetchConfigs', async (params, { rejectWithValue }) => {
    try {
        const { data } = await axiosInstance.get<FetchTieringConfigsResponse>(`/admin/hospitals/${params.hospitalId}/tiering-configs`, {
            params: {
                page: params.page,
                limit: params.limit
            }
        });

        return {
            items: data?.items ?? [],
            total: data?.total ?? 0,
            page: data?.page ?? params.page ?? 1,
            limit: data?.limit ?? params.limit ?? DEFAULT_ROWS_PER_PAGE
        };
    } catch (error: unknown) {
        return rejectWithValue(getErrorMessage(error, 'Failed to fetch tiering configs'));
    }
});

export const fetchTieringConfigById = createAsyncThunk<HospitalTieringConfig, FetchTieringConfigByIdParams, { rejectValue: string }>(
    'hospitalTiering/fetchConfigById',
    async ({ configId }, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.get<FetchTieringConfigByIdResponse>(`/admin/tiering-configs/${configId}`);
            if (!data?.item) {
                return rejectWithValue('Tiering config not found');
            }
            return data.item;
        } catch (error: unknown) {
            return rejectWithValue(getErrorMessage(error, 'Failed to fetch tiering config'));
        }
    }
);

export const fetchDataDictionaryColumns = createAsyncThunk<
    DataDictionaryColumn[],
    FetchDataDictionaryColumnsParams,
    { rejectValue: string }
>('hospitalTiering/fetchDataDictionaryColumns', async ({ hospitalId }, { rejectWithValue }) => {
    try {
        const { data } = await axiosInstance.get<FetchDataDictionaryColumnsResponse>(
            `/admin/hospitals/${hospitalId}/data-dictionary-columns`
        );
        return data?.items ?? [];
    } catch (error: unknown) {
        return rejectWithValue(getErrorMessage(error, 'Failed to fetch data dictionary columns'));
    }
});

export const createTieringConfig = createAsyncThunk<HospitalTieringConfig, CreateTieringConfigPayload, { rejectValue: string }>(
    'hospitalTiering/createConfig',
    async (payload, { rejectWithValue }) => {
        try {
            const { hospitalId, ...body } = payload;
            const { data } = await axiosInstance.post<CreateTieringConfigResponse>(`/admin/hospitals/${hospitalId}/tiering-configs`, body);
            if (!data?.item) {
                return rejectWithValue('Failed to create tiering config');
            }
            return data.item;
        } catch (error: unknown) {
            return rejectWithValue(getErrorMessage(error, 'Failed to create tiering config'));
        }
    }
);
